import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatCurrency, readFileAsDataUrl } from '@/lib/api';
import { useAuth, isAgent } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ChevronLeft, ChevronRight, Building2, User, Home, Package, Wallet, ShieldCheck, FileSignature, Plus, Trash2, Check, FileDown, FlaskConical, FileText, Sparkles, Paperclip, Eye, Image as ImageIcon, X, Upload, KeyRound, Mail, Loader2 } from 'lucide-react';
import { SignaturePad } from '@/components/SignaturePad';
import { QuoteBusinessPlanDialog } from '@/components/QuoteBusinessPlanDialog';
import { getZoneForProvince, ZONE_LABELS } from '@/lib/geoZones';

const STEPS = [
  { id: 1, label: 'Partner', icon: Building2 },
  { id: 2, label: 'Cliente', icon: User },
  { id: 3, label: 'Immobile', icon: Home },
  { id: 4, label: 'Prodotti', icon: Package },
  { id: 5, label: 'Pagamento', icon: Wallet },
  { id: 6, label: 'Firma e PDF', icon: FileSignature },
];

const emptyContract = {
  company_id: '',
  contract: { number: '', title: 'Contratto - Impianto di Efficientamento Energetico', subtitle: 'Efficientamento Energetico', date: new Date().toISOString().slice(0, 10), place: '' },
  customer: {
    customer_type: 'privato',
    first_name: '', last_name: '', birth_place: '', birth_date: '',
    company_name: '', fiscal_code: '', vat_number: '',
    pec: '', sdi_code: '', ateco_code: '',
    phone: '', email: '', legal_representative: '', legal_representative_cf: '',
    is_owner: true, ownership_title: '',
    residence_address: '', residence_city: '', residence_cap: '', residence_province: '',
    billing_address: '', billing_city: '', billing_cap: '', billing_province: '',
    iban: '',
  },
  property: { address: '', city: '', province: '', cadastral_municipality: '', cadastral_sheet: '', cadastral_parcel: '', cadastral_sub: '', cadastral_category: '' },
  products: [],
  vat_rate: 10,
  payment: { method: 'bonifico_anticipato', financing_company: '', deposit_percent: 0, second_deposit_percent: 0, balance_percent: 0, installments_count: 0, installments_amount: 0, notes: '' },
  incentives: { renovation_50: false, ecobonus: false, enea_delegation: false },
  consents: { marketing: false, profiling: false, vat_reduced_declaration: false, installer_delegation: false, clauses_approval_1341_1342: false, noicer_membership: false },
  signature: { customer_signature_data_url: '', company_signature_data_url: '', signed_place: '', signed_at: '' },
  status: 'bozza',
  is_test: false,
};

export default function NewContract() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillCustomerId = searchParams.get('customerId');
  const duplicateQuoteId = searchParams.get('duplicateQuoteId');
  const editQuoteId = searchParams.get('editQuoteId'); // apre un preventivo esistente in modalità modifica
  const initialMode = searchParams.get('mode'); // 'quote' | null
  const isQuoteMode = initialMode === 'quote' || !!searchParams.get('duplicateQuoteId') || !!editQuoteId;
  const isEditMode = !!editQuoteId;
  // In modalità preventivo lo step "Firma e PDF" non ha senso (si genera solo il PDF)
  const visibleSteps = React.useMemo(() => (
    isQuoteMode ? STEPS.filter(s => s.id !== 6) : STEPS
  ), [isQuoteMode]);
  const suggestCategory = searchParams.get('suggestCategory'); // 'wallbox' | 'accumulo' | ...
  const { user: me } = useAuth();
  const agentMode = isAgent(me);
  const [step, setStep] = useState(agentMode ? 2 : 1);
  const [data, setData] = useState(emptyContract);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [prefilledCustomerName, setPrefilledCustomerName] = useState('');
  const [quoteHintDismissed, setQuoteHintDismissed] = useState(false);
  // === Bozze auto-salvate (Phase 25.8) ===
  const DRAFT_KEY = `contract-draft:v1:${me?.id || 'anon'}:${isQuoteMode ? 'quote' : 'contract'}`;
  const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 giorni
  const [availableDraft, setAvailableDraft] = useState(null); // { savedAt, step, data, meta }
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftHydrated, setDraftHydrated] = useState(false); // true dopo la prima lettura da storage
  const suppressAutosaveRef = useRef(false);

  // Zone commerciale rilevata (aggiornata dalla provincia dell'installazione o residenza)
  const installProvince = data.installation?.property_province || data.customer.residence_province;
  const detectedZone = useMemo(() => getZoneForProvince(installProvince), [installProvince]);
  const hasZonedListino = useMemo(() => products.some(p => p.sales_zone), [products]);
  const visibleProducts = useMemo(() => {
    if (!hasZonedListino || !detectedZone) return products;
    return products.filter(p => !p.sales_zone || p.sales_zone === detectedZone);
  }, [products, hasZonedListino, detectedZone]);
  const [options, setOptions] = useState({ product_categories: [], payment_methods: [] });
  const [saving, setSaving] = useState(false);
  const [bpDialogOpen, setBpDialogOpen] = useState(false);
  const customerSigRef = useRef(null);

  // === Firma OTP ===
  const [signMode, setSignMode] = useState('manual'); // 'manual' | 'otp'
  const [otpEmail, setOtpEmail] = useState('');
  const [otpTempId, setOtpTempId] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifiedAt, setOtpVerifiedAt] = useState('');
  const [otpCodeLast4, setOtpCodeLast4] = useState('');
  const [otpMaskedEmail, setOtpMaskedEmail] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState(null); // ISO string
  const [otpNow, setOtpNow] = useState(Date.now());

  // Aggiorna il timer ogni secondo mentre l'OTP è pendente
  useEffect(() => {
    if (!otpExpiresAt || otpVerified) return undefined;
    const t = setInterval(() => setOtpNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [otpExpiresAt, otpVerified]);

  const otpRemainingMs = useMemo(() => {
    if (!otpExpiresAt) return 0;
    try { return Math.max(0, new Date(otpExpiresAt).getTime() - otpNow); }
    catch { return 0; }
  }, [otpExpiresAt, otpNow]);

  const otpExpired = otpSent && !otpVerified && otpRemainingMs <= 0;
  const otpRemainingLabel = useMemo(() => {
    if (!otpRemainingMs) return '00:00';
    const s = Math.floor(otpRemainingMs / 1000);
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [otpRemainingMs]);

  // === Ripristino bozza al mount ===
  useEffect(() => {
    if (draftHydrated) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.savedAt && (Date.now() - new Date(parsed.savedAt).getTime()) < DRAFT_MAX_AGE_MS) {
          // Se il flusso è "duplica preventivo" o "prefill customer", ignora la bozza per non confondere
          if (!prefillCustomerId && !duplicateQuoteId && !editQuoteId) {
            setAvailableDraft(parsed);
          }
        } else {
          localStorage.removeItem(DRAFT_KEY);
        }
      }
    } catch { /* ignore */ }
    setDraftHydrated(true);
  }, [DRAFT_KEY, draftHydrated, prefillCustomerId, duplicateQuoteId, editQuoteId, DRAFT_MAX_AGE_MS]);

  // === Auto-save bozza (debounced) ===
  useEffect(() => {
    if (!draftHydrated) return; // aspetta prima lettura da storage
    if (suppressAutosaveRef.current) return;
    // Non salvare wizard completamente vuoto
    const hasContent = !!(data.company_id || data.customer?.first_name || data.customer?.last_name || data.customer?.company_name || (data.products?.length ?? 0) > 0);
    if (!hasContent) return;
    const t = setTimeout(() => {
      try {
        const meta = {
          customer: (data.customer?.company_name)
            || `${data.customer?.first_name || ''} ${data.customer?.last_name || ''}`.trim()
            || 'Senza cliente',
          products_count: (data.products || []).length,
          mode: isQuoteMode ? 'quote' : 'contract',
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          savedAt: new Date().toISOString(),
          step,
          data,
          meta,
        }));
      } catch (e) { /* quota exceeded o storage disabled */ }
    }, 700);
    return () => clearTimeout(t);
  }, [data, step, DRAFT_KEY, draftHydrated, isQuoteMode]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setAvailableDraft(null);
  };
  const restoreDraft = () => {
    if (!availableDraft?.data) return;
    suppressAutosaveRef.current = true;
    setData(availableDraft.data);
    setStep(availableDraft.step || (agentMode ? 2 : 1));
    setAvailableDraft(null);
    setDraftRestored(true);
    toast.success('Bozza ripristinata');
    // Riabilita autosave dopo un tick per non salvare subito
    setTimeout(() => { suppressAutosaveRef.current = false; }, 500);
  };
  const discardDraft = () => {
    clearDraft();
    toast.info('Bozza scartata: si riparte da capo');
  };

  // Prefill dell'email cliente quando si arriva allo step 6 e non è ancora impostata
  useEffect(() => {
    if (step === 6 && !otpEmail && data.customer?.email) {
      setOtpEmail(data.customer.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const resetOtp = () => {
    setOtpTempId(''); setOtpSent(false); setOtpCode('');
    setOtpVerified(false); setOtpVerifiedAt(''); setOtpCodeLast4(''); setOtpMaskedEmail('');
    setOtpExpiresAt(null);
  };

  const handleSendOtp = async () => {
    if (!data.company_id) { toast.error('Seleziona prima l’azienda partner'); return; }
    if (!otpEmail || !otpEmail.includes('@')) { toast.error('Inserisci un’email cliente valida'); return; }
    setOtpSending(true);
    try {
      // Usa un temp_id stabile per la sessione di firma (o rigeneralo se non presente)
      const tempId = otpTempId || (crypto?.randomUUID ? crypto.randomUUID() : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      const customerName = [data.customer?.first_name, data.customer?.last_name].filter(Boolean).join(' ') || data.customer?.company_name || '';
      const res = await api.sendContractOtp({
        temp_id: tempId,
        email: otpEmail,
        customer_name: customerName,
        contract_number: data.contract?.number || '',
        company_id: data.company_id,
      });
      setOtpTempId(tempId);
      setOtpSent(true);
      setOtpMaskedEmail(res?.email_masked || '');
      if (res?.expires_at) setOtpExpiresAt(res.expires_at);
      // Se stavamo re-inviando, resetta i tentativi
      setOtpCode(''); setOtpVerified(false);
      toast.success(res?.resent ? 'Nuovo codice inviato' : 'Codice inviato via email');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile inviare il codice');
    } finally { setOtpSending(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpTempId || otpCode.length !== 6) return;
    setOtpVerifying(true);
    try {
      const res = await api.verifyContractOtp({ temp_id: otpTempId, code: otpCode });
      setOtpVerified(true);
      setOtpVerifiedAt(res?.verified_at || new Date().toISOString());
      setOtpCodeLast4(res?.otp_code_last4 || otpCode.slice(-4));
      if (res?.email_masked) setOtpMaskedEmail(res.email_masked);
      toast.success('Codice OTP verificato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Codice non valido');
    } finally { setOtpVerifying(false); }
  };

  // Filtri catalogo nel wizard (step 4) - accelerano la selezione con listini lunghi
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCat, setCatalogCat] = useState('all');
  const [catalogBrand, setCatalogBrand] = useState('all');
  const [catalogPhase, setCatalogPhase] = useState('all'); // all | monofase | trifase
  const [catalogPowerMin, setCatalogPowerMin] = useState('');
  const [catalogPowerMax, setCatalogPowerMax] = useState('');
  const [catalogKwhMin, setCatalogKwhMin] = useState('');
  const [catalogKwhMax, setCatalogKwhMax] = useState('');
  const [catalogPriceMin, setCatalogPriceMin] = useState('');
  const [catalogPriceMax, setCatalogPriceMax] = useState('');
  const [catalogPage, setCatalogPage] = useState(1);
  const CATALOG_PAGE_SIZE = 20;

  const parseNumericPower = (val) => {
    if (val == null) return null;
    const s = String(val).replace(',', '.');
    const m = s.match(/-?\d+(?:\.\d+)?/);
    if (!m) return null;
    let n = parseFloat(m[0]);
    if (Number.isNaN(n)) return null;
    if (/wp\b|watt/i.test(s) && !/kwp?\b/i.test(s)) n = n / 1000;
    return n;
  };

  const catalogBrands = useMemo(() => {
    const set = new Set();
    visibleProducts.forEach(p => { const b = (p.brand || '').trim(); if (b) set.add(b); });
    return [...set].sort((a, b) => a.localeCompare(b, 'it'));
  }, [visibleProducts]);

  const catalogFiltered = useMemo(() => {
    return visibleProducts.filter(p => {
      if (catalogCat !== 'all' && p.category !== catalogCat) return false;
      if (catalogBrand !== 'all' && (p.brand || '').trim() !== catalogBrand) return false;
      if (catalogPhase !== 'all') {
        if (!p.phase_type || p.phase_type !== catalogPhase) return false;
      }
      const pw = parseNumericPower(p.power);
      if (catalogPowerMin !== '') {
        const min = parseFloat(String(catalogPowerMin).replace(',', '.'));
        if (!Number.isNaN(min) && (pw == null || pw < min)) return false;
      }
      if (catalogPowerMax !== '') {
        const max = parseFloat(String(catalogPowerMax).replace(',', '.'));
        if (!Number.isNaN(max) && (pw == null || pw > max)) return false;
      }
      const kwh = Number(p.battery_kwh) || 0;
      if (catalogKwhMin !== '') {
        const min = parseFloat(String(catalogKwhMin).replace(',', '.'));
        if (!Number.isNaN(min) && kwh < min) return false;
      }
      if (catalogKwhMax !== '') {
        const max = parseFloat(String(catalogKwhMax).replace(',', '.'));
        if (!Number.isNaN(max) && kwh > max) return false;
      }
      const price = Number(p.unit_price) || 0;
      if (catalogPriceMin !== '') {
        const min = parseFloat(String(catalogPriceMin).replace(',', '.'));
        if (!Number.isNaN(min) && price < min) return false;
      }
      if (catalogPriceMax !== '') {
        const max = parseFloat(String(catalogPriceMax).replace(',', '.'));
        if (!Number.isNaN(max) && price > max) return false;
      }
      if (catalogSearch) {
        const t = catalogSearch.toLowerCase();
        if (![p.brand, p.model, p.description, p.category].some(s => (s || '').toLowerCase().includes(t))) return false;
      }
      return true;
    });
  }, [visibleProducts, catalogCat, catalogBrand, catalogPhase, catalogPowerMin, catalogPowerMax, catalogKwhMin, catalogKwhMax, catalogPriceMin, catalogPriceMax, catalogSearch]);

  const resetCatalogFilters = () => {
    setCatalogSearch(''); setCatalogCat('all'); setCatalogBrand('all');
    setCatalogPhase('all'); setCatalogPowerMin(''); setCatalogPowerMax('');
    setCatalogKwhMin(''); setCatalogKwhMax(''); setCatalogPriceMin(''); setCatalogPriceMax('');
    setCatalogPage(1);
  };

  // Reset catalog page when filters change
  useEffect(() => { setCatalogPage(1); }, [
    catalogCat, catalogBrand, catalogPhase, catalogPowerMin, catalogPowerMax,
    catalogKwhMin, catalogKwhMax, catalogPriceMin, catalogPriceMax, catalogSearch,
  ]);

  // Paginazione (max 20 per pagina)
  const catalogTotalPages = Math.max(1, Math.ceil(catalogFiltered.length / CATALOG_PAGE_SIZE));
  const catalogCurrentPage = Math.min(catalogPage, catalogTotalPages);
  const catalogPaged = catalogFiltered.slice((catalogCurrentPage - 1) * CATALOG_PAGE_SIZE, catalogCurrentPage * CATALOG_PAGE_SIZE);

  useEffect(() => {
    (async () => {
      try {
        const [comps, opts] = await Promise.all([api.listCompanies(), api.getOptions()]);
        setCompanies(comps); setOptions(opts);
        // Auto-select company for agent (only sees own company)
        if (agentMode && me?.company_id) {
          setData(prev => ({ ...prev, company_id: me.company_id }));
        } else if (comps.length === 1) {
          setData(prev => ({ ...prev, company_id: comps[0].id }));
        }
      } catch { toast.error('Errore caricamento dati iniziali'); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!data.company_id) { setProducts([]); return; }
    api.listProducts({ company_id: data.company_id, active: true }).then(setProducts).catch(() => setProducts([]));
  }, [data.company_id]);

  // Prefill customer data from Customer CRM (Anagrafica)
  useEffect(() => {
    if (!prefillCustomerId) return;
    (async () => {
      try {
        const c = await api.getCustomer(prefillCustomerId);
        if (!c) return;
        const isAzienda = c.customer_type === 'azienda';
        setData(prev => ({
          ...prev,
          customer: {
            ...prev.customer,
            customer_type: c.customer_type || 'privato',
            first_name: c.first_name || '',
            last_name: c.last_name || '',
            company_name: c.company_name || '',
            fiscal_code: c.fiscal_code || '',
            vat_number: c.vat_number || '',
            email: c.email || '',
            phone: c.phone || '',
            birth_place: c.birth_place || '',
            birth_date: c.birth_date || '',
            residence_address: c.address || '',
            residence_city: c.city || '',
            residence_province: c.province || '',
            residence_cap: c.cap || '',
            billing_address: c.address || '',
            billing_city: c.city || '',
            billing_province: c.province || '',
            billing_cap: c.cap || '',
            iban: c.iban || '',
            pec: c.pec || prev.customer.pec || '',
            sdi_code: c.sdi_code || prev.customer.sdi_code || '',
          },
          property: {
            ...prev.property,
            address: prev.property?.address || c.address || '',
            city: prev.property?.city || c.city || '',
            province: prev.property?.province || c.province || '',
          },
        }));
        setPrefilledCustomerName(
          (isAzienda && c.company_name)
            ? c.company_name
            : `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Cliente'
        );
        // Se l'utente è un Agente, la sua company è già assegnata → salta direttamente allo step 3 (Immobile).
        // Per Admin/Partner con più aziende, se il partner NON è ancora stato selezionato,
        // rimane sullo step 1 (Partner) così può scegliere subito senza dover tornare indietro.
        setStep(agentMode ? 3 : (data.company_id ? 3 : 1));
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Impossibile caricare il cliente selezionato');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillCustomerId]);

  // === Duplica preventivo: carica un quote esistente e ne precompila prodotti/pagamento/cliente ===
  useEffect(() => {
    const qid = duplicateQuoteId || editQuoteId;
    if (!qid) return;
    (async () => {
      try {
        const q = await api.getQuote(qid);
        if (!q) return;
        const cust = q.customer_snapshot || {};
        const isAzienda = cust.customer_type === 'azienda';
        setData(prev => ({
          ...prev,
          company_id: q.company_id || prev.company_id,
          customer: { ...prev.customer, ...cust },
          products: (q.products || []).map(p => ({
            product_id: p.product_id || null,
            category: p.category || 'accessorio',
            brand: p.brand || '',
            model: p.model || '',
            description: p.description || '',
            power: p.power || '',
            quantity: Number(p.quantity) || 1,
            unit_price: Number(p.unit_price) || 0,
            vat_rate: (p.vat_rate === null || p.vat_rate === undefined || p.vat_rate === '') ? null : Number(p.vat_rate),
            is_custom: !!p.is_custom,
            extra_items: Array.isArray(p.extra_items) ? p.extra_items : [],
            thumbnail_data_url: '',
            datasheet_data_url: '',
            datasheet_filename: '',
            datasheets: [],
          })),
          vat_rate: Number(q.vat_rate) || prev.vat_rate,
          payment: { ...prev.payment, ...(q.payment || {}) },
          notes: q.notes || prev.notes,
          title: q.title || prev.title,
        }));
        const nm = (isAzienda && cust.company_name)
          ? cust.company_name
          : `${cust.first_name || ''} ${cust.last_name || ''}`.trim() || 'Cliente';
        setPrefilledCustomerName(nm);
        // Vai allo step Prodotti per permettere la modifica — ma solo se il partner è stato
        // selezionato (o l'utente è un Agente con company già assegnata). Altrimenti riparte
        // dallo step 1 (Partner) così l'utente sceglie senza dover tornare indietro.
        const _cid = q.company_id || data.company_id;
        setStep(agentMode ? 4 : (_cid ? 4 : 1));
        toast.success(isEditMode
          ? `Preventivo ${q.number || ''} aperto: modifica e salva le modifiche`
          : 'Preventivo duplicato: modifica prodotti o prezzi e rigenera');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Impossibile caricare il preventivo');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateQuoteId, editQuoteId]);

  // === Cross-sell: quando viene indicato ?suggestCategory=wallbox|accumulo, aggiungo automaticamente
  // il primo prodotto disponibile di quella categoria al carrello prodotti, e vado allo step 4. ===
  useEffect(() => {
    if (!suggestCategory) return;
    if (!products || products.length === 0) return; // aspetta il caricamento prodotti (dipende da company_id)
    if ((data.products || []).length > 0) return; // già aggiunto qualcosa
    // Cerca prodotto in base alla categoria richiesta (accetta match parziale su category)
    const wanted = String(suggestCategory).toLowerCase();
    const match = products.find(p => (p.category || '').toLowerCase().includes(wanted) && p.active !== false);
    if (match) {
      setData(prev => ({
        ...prev,
        products: [{
          product_id: match.id,
          category: match.category,
          brand: match.brand || '',
          model: match.model || '',
          description: match.description || '',
          power: match.power || '',
          quantity: 1,
          unit_price: Number(match.unit_price) || 0,
          vat_rate: null,
          is_custom: !!match.is_custom,
          extra_items: Array.isArray(match.extra_items) ? match.extra_items : [],
          thumbnail_data_url: match.thumbnail_data_url || '',
          datasheet_data_url: match.datasheet_data_url || '',
          datasheet_filename: match.datasheet_filename || '',
          datasheets: Array.isArray(match.datasheets) ? match.datasheets : [],
        }],
      }));
      setStep(4);
      toast.success(`Suggerimento aggiunto: ${match.brand || ''} ${match.model || match.description || ''}`.trim());
    } else {
      toast.info(`Nessun prodotto di categoria "${suggestCategory}" trovato nel listino del partner`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestCategory, products]);

  const company = useMemo(() => companies.find(c => c.id === data.company_id), [companies, data.company_id]);
  const catMap = useMemo(() => Object.fromEntries((options.product_categories || []).map(c => [c.key, c.label])), [options]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    const globalRate = Number(data.vat_rate) || 0;
    for (const p of (data.products || [])) {
      const line = (Number(p.quantity) || 0) * (Number(p.unit_price) || 0);
      subtotal += line;
      const lineRate = (p.vat_rate === null || p.vat_rate === undefined || p.vat_rate === '')
        ? globalRate
        : (Number(p.vat_rate) || 0);
      vat += line * lineRate / 100;
    }
    return { subtotal, vat, total: subtotal + vat };
  }, [data.products, data.vat_rate]);

  const setField = (path, value) => {
    setData(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...(obj[keys[i]] || {}) };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const addProduct = (prod = null) => {
    setData(prev => ({
      ...prev,
      products: [...prev.products, prod ? {
        product_id: prod.id, category: prod.category, brand: prod.brand, model: prod.model,
        description: prod.description, power: prod.power, quantity: 1, unit_price: prod.unit_price,
        vat_rate: null,
        is_custom: !!prod.is_custom,
        extra_items: Array.isArray(prod.extra_items) ? prod.extra_items : [],
        thumbnail_data_url: prod.thumbnail_data_url || '',
        datasheet_data_url: prod.datasheet_data_url || '',
        datasheet_filename: prod.datasheet_filename || '',
        datasheets: Array.isArray(prod.datasheets) ? prod.datasheets : (prod.datasheet_data_url ? [{ data_url: prod.datasheet_data_url, filename: prod.datasheet_filename || 'scheda_tecnica.pdf' }] : []),
      } : { product_id: null, category: agentMode ? 'accessorio' : 'accessorio', brand: '', model: '', description: '', power: '', quantity: 1, unit_price: 0, vat_rate: null, is_custom: true, extra_items: [], thumbnail_data_url: '', datasheet_data_url: '', datasheet_filename: '', datasheets: [] }],
    }));
  };
  const removeProduct = (i) => setData(prev => ({ ...prev, products: prev.products.filter((_, j) => j !== i) }));

  // Helpers per allegare thumbnail / schede tecniche alla voce personalizzabile
  const setProductThumbnail = async (i, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Immagine troppo grande (max 2MB)'); return; }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      updateProduct(i, 'thumbnail_data_url', dataUrl);
      toast.success('Immagine di copertina aggiunta');
    } catch { toast.error('Impossibile leggere il file'); }
  };

  const addProductDatasheets = async (i, files) => {
    const pdfFiles = files.filter(f => f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)));
    if (files.length && pdfFiles.length === 0) { toast.error('Sono ammessi solo file PDF'); return; }
    const currentList = Array.isArray(data.products[i]?.datasheets) ? data.products[i].datasheets : [];
    let toAdd = [];
    for (const file of pdfFiles) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" supera i 5MB e verrà ignorato`);
        continue;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        toAdd.push({ data_url: dataUrl, filename: file.name });
      } catch { toast.error(`Impossibile leggere "${file.name}"`); }
    }
    if (toAdd.length) {
      const merged = [...currentList, ...toAdd];
      // Mirror per compatibilità: prima scheda anche nei campi legacy
      const first = merged[0];
      setData(prev => ({
        ...prev,
        products: prev.products.map((p, j) => j === i ? { ...p, datasheets: merged, datasheet_data_url: first?.data_url || '', datasheet_filename: first?.filename || '' } : p),
      }));
      toast.success(toAdd.length === 1 ? 'Scheda tecnica aggiunta' : `${toAdd.length} schede tecniche aggiunte`);
    }
  };

  const removeProductDatasheet = (i, idx) => {
    setData(prev => ({
      ...prev,
      products: prev.products.map((p, j) => {
        if (j !== i) return p;
        const arr = (p.datasheets || []).filter((_, k) => k !== idx);
        const first = arr[0];
        return { ...p, datasheets: arr, datasheet_data_url: first?.data_url || '', datasheet_filename: first?.filename || '' };
      }),
    }));
  };

  const previewProductDatasheet = (ds) => {
    if (!ds?.data_url) return;
    try {
      const [meta, base64] = ds.data_url.split(',');
      const mime = /data:(.*?);base64/.exec(meta || '')?.[1] || 'application/pdf';
      const bin = atob(base64 || '');
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossibile aprire l'anteprima"); }
  };
  const updateProduct = (i, key, value) => setData(prev => ({
    ...prev,
    products: prev.products.map((p, j) => {
      if (j !== i) return p;
      // Agents cannot modify catalog items - only quantity - UNLESS the product is flagged is_custom (personalizzabile)
      if (agentMode && p.product_id && !p.is_custom && key !== 'quantity') return p;
      // Agents creating custom rows can only use 'accessorio' category
      if (agentMode && !p.product_id && key === 'category' && value !== 'accessorio') return p;
      return { ...p, [key]: value };
    }),
  }));

  const canContinue = () => {
    // Modalità test: tutti i campi sono facoltativi
    if (data.is_test) return true;
    if (step === 1) return !!data.company_id;
    if (step === 2) {
      // Dati minimi obbligatori: identificativo + città + provincia
      // Preventivo (quote): CF/P.IVA opzionali (spesso non ancora disponibili)
      // Contratto: CF/P.IVA obbligatori
      const cs = data.customer;
      const hasIdentity = cs.customer_type === 'azienda'
        ? (isQuoteMode
            ? !!(cs.company_name)
            : !!(cs.company_name && cs.vat_number && cs.legal_representative))
        : (isQuoteMode
            ? !!(cs.first_name && cs.last_name)
            : !!(cs.first_name && cs.last_name && cs.fiscal_code));
      const hasLocation = !!(cs.residence_city?.trim() && cs.residence_province?.trim());
      if (!hasIdentity || !hasLocation) return false;
      // Nel CONTRATTO (non preventivo) sono obbligatori anche: via, cap, email, luogo/data nascita, cellulare
      if (!isQuoteMode) {
        const isPriv = cs.customer_type !== 'azienda';
        if (!cs.residence_address?.trim()) return false;
        if (!cs.residence_cap?.trim()) return false;
        if (!cs.email?.trim()) return false;
        if (!cs.phone?.trim()) return false;
        if (isPriv) {
          if (!cs.birth_place?.trim()) return false;
          if (!cs.birth_date?.trim()) return false;
        }
      }
      return true;
    }
    if (step === 3) return true;
    if (step === 4) return data.products.length > 0;
    if (step === 5) return !!data.payment.method;
    return true;
  };

  const next = () => {
    if (!canContinue()) { toast.error('Completa i campi obbligatori'); return; }
    setStep(s => Math.min(visibleSteps[visibleSteps.length - 1]?.id || STEPS.length, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => { setStep(s => Math.max(agentMode ? 2 : 1, s - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const saveContract = async (withSignature = false) => {
    setSaving(true);
    try {
      let payload = { ...data };
      if (withSignature) {
        // Firma azienda: sempre applicata dal timbro & firma configurato dal partner (backend override).
        // Non trasmettiamo dati manuali per evitare conflitti.
        if (signMode === 'otp') {
          if (!otpVerified && !data.is_test) { toast.error('Verifica il codice OTP prima di generare il contratto firmato'); setSaving(false); return null; }
          payload = {
            ...payload,
            signature: {
              ...payload.signature,
              signature_mode: 'otp',
              otp_temp_id: otpTempId,
              otp_email: otpEmail,
              otp_verified_at: otpVerifiedAt,
              otp_code_last4: otpCodeLast4,
              customer_signature_data_url: '',
              company_signature_data_url: '',
              signed_at: data.contract.date,
            },
            status: 'firmato',
          };
        } else {
          const custUrl = customerSigRef.current?.getDataUrl() || data.signature.customer_signature_data_url;
          if (!custUrl && !data.is_test) { toast.error('La firma del cliente è obbligatoria'); setSaving(false); return null; }
          payload = {
            ...payload,
            signature: {
              ...payload.signature,
              signature_mode: 'manual',
              customer_signature_data_url: custUrl || '',
              company_signature_data_url: '',
              signed_at: data.contract.date,
              otp_temp_id: '', otp_email: '', otp_verified_at: '', otp_code_last4: '',
            },
            status: 'firmato',
          };
        }
      } else {
        payload = { ...payload, status: payload.status === 'bozza' ? 'compilato' : payload.status };
      }
      const created = await api.createContract(payload);
      toast.success(data.is_test ? 'Contratto di test creato (non conteggiato nelle statistiche)' : 'Contratto creato');
      clearDraft();
      return created;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione contratto');
      return null;
    } finally { setSaving(false); }
  };

  const handleGeneratePdf = async (mode) => {
    const created = await saveContract(mode === 'signed');
    if (!created) return;
    try { await api.openContractPdf(created.id, mode); } catch (e) { toast.error('Impossibile aprire il PDF'); }
    setTimeout(() => navigate(`/contracts/${created.id}`), 600);
  };

  const handleSaveDraft = async () => {
    const created = await saveContract(false);
    if (created) navigate(`/contracts/${created.id}`);
  };

  const handleGenerateQuote = async () => {
    if (!data.company_id) {
      toast.error('Seleziona prima un\'azienda partner');
      return;
    }
    if (!data.products || data.products.length === 0) {
      toast.error('Aggiungi almeno un prodotto per generare il preventivo');
      return;
    }
    setBpDialogOpen(true);
  };

  const buildQuotePayload = () => ({
    company_id: data.company_id,
    title: 'Soluzione di Efficientamento Energetico',
    date: data.contract.date,
    place: data.contract.place,
    customer: data.customer,
    products: data.products,
    vat_rate: data.vat_rate,
    payment: data.payment,
    notes: data.payment?.notes || '',
  });

  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Modifica preventivo' : (duplicateQuoteId ? 'Duplica preventivo' : (initialMode === 'quote' ? 'Nuovo preventivo' : 'Nuovo contratto'))}
        description="Compila in 6 passaggi i dati del cliente, prodotti, modalità di pagamento e firma"
      />

      {availableDraft && !draftRestored && (
        <Card className="mb-4 border-amber-500/50 bg-amber-500/5" data-testid="draft-restore-banner">
          <CardContent className="p-3 flex items-start sm:items-center gap-3 flex-col sm:flex-row">
            <div className="size-9 rounded-full grid place-items-center shrink-0 bg-amber-500/20 text-amber-700">
              <FileText className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                Bozza precedente disponibile · <span className="text-primary">{availableDraft.meta?.customer || 'Senza cliente'}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Salvata {(() => {
                  try {
                    const dt = new Date(availableDraft.savedAt);
                    return dt.toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                  } catch { return availableDraft.savedAt; }
                })()} · Step {availableDraft.step || 1} · {availableDraft.meta?.products_count || 0} prodotti
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={discardDraft} data-testid="draft-discard">
                <X className="size-4 mr-1" /> Ricomincia
              </Button>
              <Button size="sm" onClick={restoreDraft} data-testid="draft-restore">
                <FileText className="size-4 mr-1" /> Ripristina bozza
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {draftRestored && (
        <div className="mb-3 text-xs text-muted-foreground flex items-center gap-2" data-testid="draft-restored-notice">
          <Sparkles className="size-3.5 text-primary" /> Bozza ripristinata · Le modifiche vengono salvate automaticamente ogni pochi secondi.
        </div>
      )}

      {prefilledCustomerName && !quoteHintDismissed && (
        <Card className="mb-4 border-primary/40 bg-primary/5" data-testid="customer-prefill-hint">
          <CardContent className="p-3 flex items-start sm:items-center gap-3 flex-col sm:flex-row">
            <div className="size-9 rounded-full grid place-items-center shrink-0 bg-primary/15 text-primary">
              <User className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {duplicateQuoteId
                  ? <>Duplicando preventivo per: <span className="text-primary">{prefilledCustomerName}</span></>
                  : (initialMode === 'quote' ? 'Preventivo per: ' : 'Contratto per: ')}
                {!duplicateQuoteId && <span className="text-primary">{prefilledCustomerName}</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {duplicateQuoteId
                  ? 'Prodotti, pagamento e dati cliente precompilati. Modifica ciò che vuoi e rigenera il preventivo.'
                  : (suggestCategory
                    ? `Prodotto suggerito (${suggestCategory}) aggiunto dal listino. Verifica quantità/prezzo prima di generare il preventivo.`
                    : 'Dati anagrafici precompilati dall\'anagrafica cliente. Puoi comunque modificarli nello step Cliente.')}
                {initialMode === 'quote' && !duplicateQuoteId && ' Aggiungi i prodotti e clicca “Genera Preventivo” in alto.'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setQuoteHintDismissed(true)} data-testid="dismiss-prefill-hint">
              <X className="size-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Test mode toggle + Quote (Preventivo) shortcut */}
      <Card className={`mb-4 border-dashed ${data.is_test ? 'border-amber-400 bg-amber-50/40 dark:bg-amber-500/5' : ''}`} data-testid="test-mode-banner">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-start sm:items-center gap-3 flex-1">
            <div className={`size-9 rounded-full grid place-items-center shrink-0 ${data.is_test ? 'bg-amber-500/15 text-amber-600 dark:text-amber-300' : 'bg-secondary text-muted-foreground'}`}>
              <FlaskConical className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Label htmlFor="is-test-switch" className="text-sm font-medium cursor-pointer">È un test</Label>
                <Switch
                  id="is-test-switch"
                  checked={!!data.is_test}
                  onCheckedChange={(v) => setField('is_test', !!v)}
                  data-testid="is-test-switch"
                />
                {data.is_test && (
                  <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300 bg-amber-500/10">
                    Modalità test attiva
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.is_test
                  ? 'Nessun campo è obbligatorio. Il contratto sarà marcato come test e non verrà conteggiato nelle statistiche ufficiali.'
                  : 'Attiva la modalità test per provare il flusso senza vincoli sui campi obbligatori.'}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={handleGenerateQuote}
            disabled={saving || !data.company_id || data.products.length === 0}
            data-testid="generate-quote-pdf"
            className="shrink-0"
          >
            <Sparkles className="size-4 mr-2" />Genera Preventivo
          </Button>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <Progress value={(step / visibleSteps.length) * 100} className="mb-3 h-2" />
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="contract-wizard-stepper">
            {visibleSteps.filter(s => !(agentMode && s.id === 1)).map(s => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div key={s.id} className={`flex items-center gap-2 px-2 py-2 rounded-lg text-xs ${active ? 'bg-primary text-primary-foreground' : done ? 'bg-secondary text-foreground' : 'text-muted-foreground bg-transparent'}`}>
                  <div className={`size-6 rounded-full grid place-items-center text-xs font-semibold ${active ? 'bg-primary-foreground/20' : done ? 'bg-foreground/10' : 'bg-secondary/60'}`}>
                    {done ? <Check className="size-3" /> : s.id}
                  </div>
                  <span className="truncate hidden sm:inline">{s.label}</span>
                </div>
              );
            })}
          </div>
          {agentMode && companies.length > 0 && (
            <div className="mt-3 text-xs text-muted-foreground">Stai operando per <span className="font-medium text-foreground">{companies.find(c => c.id === data.company_id)?.name || 'la tua azienda'}</span></div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {step === 1 && (
            <Card>
              <CardHeader><CardTitle className="text-base">1. Seleziona l’azienda partner</CardTitle></CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nessuna azienda partner trovata. <a href="/partners" className="underline">Creane una</a>.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {companies.map(c => (
                      <button key={c.id} onClick={() => setField('company_id', c.id)} className={`text-left rounded-xl border p-4 hover:bg-secondary/40 transition-colors ${data.company_id === c.id ? 'border-primary bg-secondary/50' : 'bg-card'}`} data-testid={`partner-card-${c.id}`}>
                        <div className="flex items-center gap-3">
                          {c.logo_data_url ? <img src={c.logo_data_url} alt="" className="size-12 rounded border object-contain bg-white p-1" /> : <div className="size-12 rounded border bg-secondary grid place-items-center"><Building2 className="size-5 text-muted-foreground" /></div>}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{c.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{c.city} {c.province && `(${c.province})`}</div>
                          </div>
                          {data.company_id === c.id && <Check className="size-5 text-primary ml-auto" />}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHeader><CardTitle className="text-base">2. Dati del Cliente</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Tipo cliente: Privato / Azienda */}
                <div>
                  <Label className="text-xs mb-2 block">Tipo cliente</Label>
                  <div className="inline-flex rounded-lg border p-0.5 bg-secondary/50">
                    <button
                      type="button"
                      onClick={() => setField('customer.customer_type', 'privato')}
                      className={`px-4 h-8 rounded-md text-xs font-medium transition-colors ${(data.customer.customer_type || 'privato') === 'privato' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid="customer-type-privato"
                    >Privato</button>
                    <button
                      type="button"
                      onClick={() => setField('customer.customer_type', 'azienda')}
                      className={`px-4 h-8 rounded-md text-xs font-medium transition-colors ${data.customer.customer_type === 'azienda' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid="customer-type-azienda"
                    >Azienda</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.customer.customer_type === 'azienda' ? (
                    <>
                      <Field label="Ragione sociale *" full><Input value={data.customer.company_name} onChange={(e) => setField('customer.company_name', e.target.value)} data-testid="customer-company-name" /></Field>
                      <Field label={`Partita IVA${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.vat_number} onChange={(e) => setField('customer.vat_number', e.target.value)} className="font-mono" /></Field>
                      <Field label="Codice Fiscale"><Input value={data.customer.fiscal_code} onChange={(e) => setField('customer.fiscal_code', e.target.value.toUpperCase())} className="font-mono" /></Field>
                      <Field label={`Rappresentante legale${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.legal_representative} onChange={(e) => setField('customer.legal_representative', e.target.value)} /></Field>
                      <Field label="CF rappresentante"><Input value={data.customer.legal_representative_cf} onChange={(e) => setField('customer.legal_representative_cf', e.target.value.toUpperCase())} className="font-mono" /></Field>
                      <Field label="PEC"><Input type="email" value={data.customer.pec || ''} onChange={(e) => setField('customer.pec', e.target.value)} placeholder="pec@azienda.it" data-testid="customer-pec" /></Field>
                      <Field label="Codice destinatario SDI"><Input value={data.customer.sdi_code || ''} onChange={(e) => setField('customer.sdi_code', e.target.value.toUpperCase())} maxLength={7} className="font-mono uppercase" placeholder="XXXXXXX" data-testid="customer-sdi" /></Field>
                      <Field label={`Telefono${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.phone} onChange={(e) => setField('customer.phone', e.target.value)} data-testid="customer-phone" /></Field>
                      <Field label={`Email${isQuoteMode ? '' : ' *'}`}><Input type="email" value={data.customer.email} onChange={(e) => setField('customer.email', e.target.value)} data-testid="customer-email" /></Field>
                    </>
                  ) : (
                    <>
                      <Field label="Nome *"><Input value={data.customer.first_name} onChange={(e) => setField('customer.first_name', e.target.value)} data-testid="customer-first-name" /></Field>
                      <Field label="Cognome *"><Input value={data.customer.last_name} onChange={(e) => setField('customer.last_name', e.target.value)} data-testid="customer-last-name" /></Field>
                      <Field label={`Codice Fiscale${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.fiscal_code} onChange={(e) => setField('customer.fiscal_code', e.target.value.toUpperCase())} className="font-mono" data-testid="customer-fiscal-code" /></Field>
                      <Field label={`Luogo di nascita${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.birth_place} onChange={(e) => setField('customer.birth_place', e.target.value)} data-testid="customer-birth-place" /></Field>
                      <Field label={`Data di nascita${isQuoteMode ? '' : ' *'}`}><Input type="date" value={data.customer.birth_date} onChange={(e) => setField('customer.birth_date', e.target.value)} data-testid="customer-birth-date" /></Field>
                      <Field label={`Telefono${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.phone} onChange={(e) => setField('customer.phone', e.target.value)} data-testid="customer-phone" /></Field>
                      <Field label={`Email${isQuoteMode ? '' : ' *'}`}><Input type="email" value={data.customer.email} onChange={(e) => setField('customer.email', e.target.value)} data-testid="customer-email" /></Field>
                    </>
                  )}
                </div>

                <Separator />
                <div className="flex items-center gap-3">
                  <Switch checked={data.customer.is_owner} onCheckedChange={(v) => setField('customer.is_owner', v)} />
                  <Label>{data.customer.customer_type === 'azienda' ? 'L\'azienda è proprietaria' : 'Il cliente è proprietario'} dell&apos;immobile</Label>
                </div>
                {!data.customer.is_owner && (
                  <Field label="Titolo sull'immobile"><Input value={data.customer.ownership_title} onChange={(e) => setField('customer.ownership_title', e.target.value)} placeholder="Es. usufruttuario, comodatario, locatario..." /></Field>
                )}

                <Separator />
                <div className="text-sm font-medium">
                  Residenza
                  <span className="text-xs text-muted-foreground font-normal ml-1">
                    {isQuoteMode ? '(Città e Provincia obbligatorie)' : '(Indirizzo, CAP, Città e Provincia obbligatori)'}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Field label={`Indirizzo${isQuoteMode ? '' : ' *'}`} full><Input value={data.customer.residence_address} onChange={(e) => setField('customer.residence_address', e.target.value)} data-testid="customer-residence-address" /></Field>
                  <Field label="Località *"><Input value={data.customer.residence_city} onChange={(e) => setField('customer.residence_city', e.target.value)} data-testid="customer-residence-city" /></Field>
                  <Field label={`CAP${isQuoteMode ? '' : ' *'}`}><Input value={data.customer.residence_cap} onChange={(e) => setField('customer.residence_cap', e.target.value)} maxLength={5} data-testid="customer-residence-cap" /></Field>
                  <Field label="Prov. *"><Input value={data.customer.residence_province} onChange={(e) => setField('customer.residence_province', e.target.value.toUpperCase())} maxLength={2} data-testid="customer-residence-province" /></Field>
                </div>

                <div className="text-sm font-medium">Fatturazione</div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    setField('customer.billing_address', data.customer.residence_address);
                    setField('customer.billing_city', data.customer.residence_city);
                    setField('customer.billing_cap', data.customer.residence_cap);
                    setField('customer.billing_province', data.customer.residence_province);
                  }}>Copia da residenza</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Field label="Indirizzo" full><Input value={data.customer.billing_address} onChange={(e) => setField('customer.billing_address', e.target.value)} /></Field>
                  <Field label="Località"><Input value={data.customer.billing_city} onChange={(e) => setField('customer.billing_city', e.target.value)} /></Field>
                  <Field label="CAP"><Input value={data.customer.billing_cap} onChange={(e) => setField('customer.billing_cap', e.target.value)} maxLength={5} /></Field>
                  <Field label="Prov."><Input value={data.customer.billing_province} onChange={(e) => setField('customer.billing_province', e.target.value.toUpperCase())} maxLength={2} /></Field>
                </div>

                <Separator />
                <Field label="IBAN (per rimborsi GSE/finanziamento)"><Input value={data.customer.iban} onChange={(e) => setField('customer.iban', e.target.value.toUpperCase().replace(/\s/g, ''))} className="font-mono" placeholder="IT60X0542811101000000123456" /></Field>
              </CardContent>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <CardHeader><CardTitle className="text-base">3. Dati Immobile e Catastali</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Indirizzo immobile" full><Input value={data.property.address} onChange={(e) => setField('property.address', e.target.value)} data-testid="property-address" /></Field>
                  <Field label="Comune"><Input value={data.property.city} onChange={(e) => setField('property.city', e.target.value)} /></Field>
                  <Field label="Provincia"><Input value={data.property.province} onChange={(e) => setField('property.province', e.target.value.toUpperCase())} maxLength={2} /></Field>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => {
                  setField('property.address', data.customer.residence_address);
                  setField('property.city', data.customer.residence_city);
                  setField('property.province', data.customer.residence_province);
                }}>Copia da residenza cliente</Button>
                <Separator />
                <div className="text-sm font-medium">Dati catastali</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Field label="Comune catastale" full><Input value={data.property.cadastral_municipality} onChange={(e) => setField('property.cadastral_municipality', e.target.value)} /></Field>
                  <Field label="Foglio"><Input value={data.property.cadastral_sheet} onChange={(e) => setField('property.cadastral_sheet', e.target.value)} /></Field>
                  <Field label="Particella"><Input value={data.property.cadastral_parcel} onChange={(e) => setField('property.cadastral_parcel', e.target.value)} /></Field>
                  <Field label="Subalterno"><Input value={data.property.cadastral_sub} onChange={(e) => setField('property.cadastral_sub', e.target.value)} /></Field>
                  <Field label="Categoria"><Input value={data.property.cadastral_category} onChange={(e) => setField('property.cadastral_category', e.target.value)} placeholder="A/3" /></Field>
                </div>
                <Separator />
                <div className="text-sm font-medium">Incentivi e detrazioni</div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.incentives.renovation_50} onCheckedChange={(v) => setField('incentives.renovation_50', !!v)} /> Ristrutturazione edilizia 50% (art. 16-bis TUIR)</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.incentives.ecobonus} onCheckedChange={(v) => setField('incentives.ecobonus', !!v)} /> Ecobonus</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.incentives.enea_delegation} onCheckedChange={(v) => setField('incentives.enea_delegation', !!v)} /> Delega comunicazione ENEA</label>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 4 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-base">4. Prodotti e Servizi</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Aliquota IVA (predefinita)</Label>
                  <Select value={String(data.vat_rate)} onValueChange={(v) => setField('vat_rate', Number(v))}>
                    <SelectTrigger className="w-[170px] h-9" data-testid="vat-rate-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (rev. charge / esente)</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="5">5% (super-ridotta)</SelectItem>
                      <SelectItem value="10">10% (agevolata)</SelectItem>
                      <SelectItem value="22">22% (ordinaria)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Banner zona geografica rilevata (solo se il listino è zonizzato) */}
                {hasZonedListino && (
                  <div className={`px-3 py-2 rounded-lg border text-xs flex items-center gap-2 ${detectedZone ? 'bg-primary/5 border-primary/30' : 'bg-amber-500/5 border-amber-500/30'}`} data-testid="wizard-zone-banner">
                    <span className={`inline-flex items-center justify-center size-5 rounded-full ${detectedZone ? 'bg-primary/15 text-primary' : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>📍</span>
                    {detectedZone ? (
                      <span>Listino <strong>{ZONE_LABELS[detectedZone]}</strong> applicato — {visibleProducts.length} prodotti disponibili per la provincia <strong>{installProvince}</strong></span>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-300">Compila la <strong>provincia di installazione o residenza</strong> nel passo precedente per applicare il listino della zona corretta</span>
                    )}
                  </div>
                )}
                {visibleProducts.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="text-sm font-medium">Aggiungi dal catalogo</div>
                      <div className="text-xs text-muted-foreground">{catalogFiltered.length} di {visibleProducts.length}</div>
                    </div>
                    {/* Barra filtri per catalogo (accelera la selezione con listini lunghi) */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
                      <div className="md:col-span-4">
                        <Input
                          placeholder="Cerca marca, modello, descrizione..."
                          value={catalogSearch}
                          onChange={(e) => setCatalogSearch(e.target.value)}
                          className="h-9"
                          data-testid="catalog-search"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Select value={catalogCat} onValueChange={setCatalogCat}>
                          <SelectTrigger className="h-9" data-testid="catalog-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutte le categorie</SelectItem>
                            {(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Select value={catalogBrand} onValueChange={setCatalogBrand}>
                          <SelectTrigger className="h-9" data-testid="catalog-filter-brand"><SelectValue placeholder="Marca" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutte le marche</SelectItem>
                            {catalogBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3 flex items-center gap-2">
                        <div className="inline-flex rounded-lg border p-0.5 bg-secondary/30 h-9" data-testid="catalog-filter-phase">
                          {[
                            { key: 'all', label: 'Tutte' },
                            { key: 'monofase', label: 'Monofase' },
                            { key: 'trifase', label: 'Trifase' },
                          ].map(opt => (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => setCatalogPhase(opt.key)}
                              className={`px-2.5 h-8 rounded-md text-xs font-medium transition-colors ${catalogPhase === opt.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                              data-testid={`catalog-filter-phase-${opt.key}`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                      <div className="md:col-span-3 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">kWp:</span>
                        <Input type="number" inputMode="decimal" placeholder="min" value={catalogPowerMin} onChange={(e) => setCatalogPowerMin(e.target.value)} className="h-9 w-16 tabular-nums" data-testid="catalog-filter-power-min" />
                        <span className="text-muted-foreground">—</span>
                        <Input type="number" inputMode="decimal" placeholder="max" value={catalogPowerMax} onChange={(e) => setCatalogPowerMax(e.target.value)} className="h-9 w-16 tabular-nums" data-testid="catalog-filter-power-max" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
                      <div className="md:col-span-4 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">kWh (batt.):</span>
                        <Input type="number" inputMode="decimal" placeholder="min" value={catalogKwhMin} onChange={(e) => setCatalogKwhMin(e.target.value)} className="h-9 w-20 tabular-nums" data-testid="catalog-filter-kwh-min" />
                        <span className="text-muted-foreground">—</span>
                        <Input type="number" inputMode="decimal" placeholder="max" value={catalogKwhMax} onChange={(e) => setCatalogKwhMax(e.target.value)} className="h-9 w-20 tabular-nums" data-testid="catalog-filter-kwh-max" />
                      </div>
                      <div className="md:col-span-5 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">€ prezzo:</span>
                        <Input type="number" inputMode="decimal" placeholder="min" value={catalogPriceMin} onChange={(e) => setCatalogPriceMin(e.target.value)} className="h-9 w-24 tabular-nums" data-testid="catalog-filter-price-min" />
                        <span className="text-muted-foreground">—</span>
                        <Input type="number" inputMode="decimal" placeholder="max" value={catalogPriceMax} onChange={(e) => setCatalogPriceMax(e.target.value)} className="h-9 w-24 tabular-nums" data-testid="catalog-filter-price-max" />
                      </div>
                      <div className="md:col-span-3 flex items-center justify-end gap-2">
                        {(catalogSearch || catalogCat !== 'all' || catalogBrand !== 'all' || catalogPhase !== 'all' || catalogPowerMin || catalogPowerMax || catalogKwhMin || catalogKwhMax || catalogPriceMin || catalogPriceMax) && (
                          <Button type="button" variant="ghost" size="sm" onClick={resetCatalogFilters} title="Reset filtri" data-testid="catalog-filter-reset">
                            <X className="size-4 mr-1" />Reset filtri
                          </Button>
                        )}
                      </div>
                    </div>
                    {catalogFiltered.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg text-center">
                        Nessun prodotto trovato con i filtri correnti
                      </div>
                    ) : (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
                        {catalogPaged.map(p => (
                          <button key={p.id} type="button" onClick={() => addProduct(p)} className={`text-left rounded-lg border p-3 hover:bg-secondary/40 transition-colors ${p.is_custom ? 'border-accent/40' : ''}`} data-testid={`add-product-${p.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  <span>{catMap[p.category] || p.category}</span>
                                  {p.is_custom && <Badge variant="outline" className="gap-1 text-[9px] h-4 px-1 border-accent text-accent"><Sparkles className="size-2.5" />Personalizzabile</Badge>}
                                </div>
                                <div className="font-medium truncate text-sm">{p.brand} {p.model}</div>
                                <div className="text-xs text-muted-foreground truncate">{p.description}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium tabular-nums">{formatCurrency(p.unit_price)}</div>
                                <Plus className="size-4 inline mt-1 text-primary" />
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                      {catalogTotalPages > 1 && (
                        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap" data-testid="catalog-pagination">
                          <div className="text-xs text-muted-foreground">
                            Mostrando {(catalogCurrentPage - 1) * CATALOG_PAGE_SIZE + 1}-{Math.min(catalogCurrentPage * CATALOG_PAGE_SIZE, catalogFiltered.length)} di {catalogFiltered.length}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button type="button" variant="outline" size="sm" disabled={catalogCurrentPage === 1} onClick={() => setCatalogPage(1)}>«</Button>
                            <Button type="button" variant="outline" size="sm" disabled={catalogCurrentPage === 1} onClick={() => setCatalogPage(p => Math.max(1, p - 1))}>‹</Button>
                            <span className="px-2 text-xs tabular-nums">Pag. {catalogCurrentPage} / {catalogTotalPages}</span>
                            <Button type="button" variant="outline" size="sm" disabled={catalogCurrentPage === catalogTotalPages} onClick={() => setCatalogPage(p => Math.min(catalogTotalPages, p + 1))}>›</Button>
                            <Button type="button" variant="outline" size="sm" disabled={catalogCurrentPage === catalogTotalPages} onClick={() => setCatalogPage(catalogTotalPages)}>»</Button>
                          </div>
                        </div>
                      )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Prodotti in contratto ({data.products.length})</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => addProduct()} data-testid="add-custom-product"><Plus className="size-4 mr-1" />{agentMode ? 'Aggiungi accessorio' : 'Riga personalizzata'}</Button>
                </div>

                {data.products.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-6 border border-dashed rounded-lg text-center">Aggiungi almeno un prodotto al contratto</div>
                ) : (
                  <div className="space-y-3">
                    {data.products.map((p, i) => (
                      <div key={i} className="rounded-lg border p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                        <div className="md:col-span-2"><Label className="text-xs">Categoria</Label>
                          <Select value={p.category} onValueChange={(v) => updateProduct(i, 'category', v)}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2"><Label className="text-xs">Marca</Label><Input value={p.brand} onChange={(e) => updateProduct(i, 'brand', e.target.value)} className="h-9" /></div>
                        <div className="md:col-span-2"><Label className="text-xs">Modello</Label><Input value={p.model} onChange={(e) => updateProduct(i, 'model', e.target.value)} className="h-9" /></div>
                        <div className="md:col-span-2"><Label className="text-xs">Descrizione</Label><Input value={p.description} onChange={(e) => updateProduct(i, 'description', e.target.value)} className="h-9" /></div>
                        <div className="md:col-span-1"><Label className="text-xs">Q.tà</Label><Input type="number" step="1" min="0" value={p.quantity} onChange={(e) => updateProduct(i, 'quantity', Number(e.target.value))} className="h-9 tabular-nums" data-testid={`product-qty-${i}`} /></div>
                        <div className="md:col-span-1"><Label className="text-xs">Prezzo</Label><Input type="number" step="0.01" min="0" value={p.unit_price} onChange={(e) => updateProduct(i, 'unit_price', Number(e.target.value))} className="h-9 tabular-nums" data-testid={`product-price-${i}`} /></div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Regime IVA</Label>
                          <Select
                            value={p.vat_rate === null || p.vat_rate === undefined || p.vat_rate === '' ? 'default' : String(p.vat_rate)}
                            onValueChange={(v) => updateProduct(i, 'vat_rate', v === 'default' ? null : Number(v))}
                          >
                            <SelectTrigger className="h-9" data-testid={`product-vat-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Predefinita ({data.vat_rate}%)</SelectItem>
                              <SelectItem value="0">0% (rev. charge / esente)</SelectItem>
                              <SelectItem value="4">4%</SelectItem>
                              <SelectItem value="5">5% (super-ridotta)</SelectItem>
                              <SelectItem value="10">10% (agevolata)</SelectItem>
                              <SelectItem value="22">22% (ordinaria)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Voci addizionali incluse: dichiarazione di conformità, pratiche, allaccio, sopralluogo, ecc. */}
                        <div className="md:col-span-12 border-t pt-2">
                          <Label className="text-xs flex items-center gap-2">
                            <FileText className="size-3.5" />
                            Voci addizionali incluse nel prodotto/servizio
                            <span className="text-muted-foreground font-normal">(es. dichiarazione di conformità, pratiche, allaccio, sopralluogo, DDT, collaudo)</span>
                          </Label>
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            {(p.extra_items || []).map((it, idx) => (
                              <Badge key={idx} variant="secondary" className="gap-1 pr-1 py-1" data-testid={`product-extra-${i}-${idx}`}>
                                <span>{it}</span>
                                <button
                                  type="button"
                                  onClick={() => updateProduct(i, 'extra_items', (p.extra_items || []).filter((_, k) => k !== idx))}
                                  className="hover:text-destructive ml-1"
                                  aria-label="Rimuovi voce"
                                >
                                  <X className="size-3" />
                                </button>
                              </Badge>
                            ))}
                            <Input
                              placeholder="Aggiungi voce e premi Invio…"
                              className="h-8 w-64"
                              data-testid={`product-extra-input-${i}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value.trim()) {
                                  e.preventDefault();
                                  const v = e.target.value.trim();
                                  updateProduct(i, 'extra_items', [...(p.extra_items || []), v]);
                                  e.target.value = '';
                                }
                              }}
                            />
                            {['Dichiarazione di conformità', 'Pratiche GSE', 'Allaccio', 'Sopralluogo tecnico', 'Sviluppo e assistenza pratica AGRISOLARE', 'Monitoraggio Impianto', 'Gestione ricariche auto in Roaming', 'Progettazione e oneri di sicurezza'].map(sugg => (
                              !(p.extra_items || []).includes(sugg) && (
                                <button
                                  key={sugg}
                                  type="button"
                                  onClick={() => updateProduct(i, 'extra_items', [...(p.extra_items || []), sugg])}
                                  className="text-[11px] px-2 py-0.5 rounded-full border border-dashed hover:bg-secondary text-muted-foreground"
                                  data-testid={`product-extra-quick-${i}-${sugg.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  + {sugg}
                                </button>
                              )
                            ))}
                          </div>
                        </div>

                        {/* Allegati per voce personalizzabile */}
                        {p.is_custom && (
                          <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-3 border-t pt-3" data-testid={`custom-product-attachments-${i}`}>
                            {/* Copertina */}
                            <div className="space-y-2">
                              <Label className="text-xs flex items-center gap-1.5"><ImageIcon className="size-3.5" />Immagine di copertina</Label>
                              {p.thumbnail_data_url ? (
                                <div className="flex items-center gap-2 rounded-md border p-2 bg-secondary/30">
                                  <img src={p.thumbnail_data_url} alt="" className="size-10 rounded object-contain bg-white border shrink-0" />
                                  <div className="text-xs text-muted-foreground truncate flex-1">Immagine caricata</div>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => updateProduct(i, 'thumbnail_data_url', '')} aria-label="Rimuovi immagine" data-testid={`custom-remove-thumb-${i}`}>
                                    <X className="size-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <label className="flex items-center gap-2 rounded-md border border-dashed p-2 cursor-pointer hover:bg-secondary/30 text-xs text-muted-foreground">
                                  <Upload className="size-3.5" />
                                  <span>Carica PNG / JPG / WebP · max 2MB</span>
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    className="sr-only"
                                    data-testid={`custom-upload-thumb-${i}`}
                                    onChange={async (e) => { const f = e.target.files?.[0]; await setProductThumbnail(i, f); e.target.value = ''; }}
                                  />
                                </label>
                              )}
                            </div>

                            {/* Schede tecniche multiple */}
                            <div className="space-y-2">
                              <Label className="text-xs flex items-center gap-1.5"><Paperclip className="size-3.5" />Schede tecniche / allegati PDF</Label>
                              {Array.isArray(p.datasheets) && p.datasheets.length > 0 && (
                                <ul className="space-y-1.5">
                                  {p.datasheets.map((ds, idx) => (
                                    <li key={idx} className="flex items-center gap-2 rounded-md border p-2 bg-secondary/30 text-xs">
                                      <FileText className="size-3.5 text-primary shrink-0" />
                                      <span className="truncate flex-1" title={ds.filename}>{ds.filename || `scheda_${idx + 1}.pdf`}</span>
                                      <Button type="button" variant="ghost" size="icon" className="size-7" onClick={() => previewProductDatasheet(ds)} aria-label="Anteprima" data-testid={`custom-preview-ds-${i}-${idx}`}>
                                        <Eye className="size-3.5" />
                                      </Button>
                                      <Button type="button" variant="ghost" size="icon" className="size-7" onClick={() => removeProductDatasheet(i, idx)} aria-label="Rimuovi" data-testid={`custom-remove-ds-${i}-${idx}`}>
                                        <X className="size-3.5" />
                                      </Button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <label className="flex items-center gap-2 rounded-md border border-dashed p-2 cursor-pointer hover:bg-secondary/30 text-xs text-muted-foreground">
                                <Upload className="size-3.5" />
                                <span>Trascina o carica uno o più PDF · max 5MB ciascuno</span>
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  multiple
                                  className="sr-only"
                                  data-testid={`custom-upload-ds-${i}`}
                                  onChange={async (e) => { const files = Array.from(e.target.files || []); await addProductDatasheets(i, files); e.target.value = ''; }}
                                />
                              </label>
                            </div>
                          </div>
                        )}

                        <div className="md:col-span-12 flex items-center justify-between border-t pt-2 mt-1">
                          <div className="text-sm text-muted-foreground">
                            {p.is_custom && <Badge variant="outline" className="gap-1 text-[10px] border-accent text-accent mr-2"><Sparkles className="size-3" />Voce personalizzabile</Badge>}
                            Subtotale riga
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="font-medium tabular-nums">{formatCurrency((Number(p.quantity) || 0) * (Number(p.unit_price) || 0))}</div>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeProduct(i)} data-testid={`remove-product-${i}`}><Trash2 className="size-4 text-destructive" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {step === 5 && (
            <Card>
              <CardHeader><CardTitle className="text-base">5. Modalità di pagamento</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={data.payment.method} onValueChange={(v) => {
                  setField('payment.method', v);
                  // Reset percentuali (l'utente le decide manualmente)
                  if (v !== 'acconto_saldo' && v !== 'doppio_acconto_saldo') {
                    setField('payment.second_deposit_percent', 0);
                  }
                }} className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="payment-method-radio">
                  {(options.payment_methods || []).map(m => (
                    <label key={m.key} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-secondary/40 ${data.payment.method === m.key ? 'border-primary bg-secondary/50' : ''}`}>
                      <RadioGroupItem value={m.key} id={m.key} />
                      <span className="text-sm font-medium">{m.label}</span>
                    </label>
                  ))}
                </RadioGroup>

                {data.payment.method === 'finanziamento' && (
                  <Field label="Società finanziaria"><Input value={data.payment.financing_company} onChange={(e) => setField('payment.financing_company', e.target.value)} placeholder="Es. Findomestic, Agos..." data-testid="financing-company-input" /></Field>
                )}
                {data.payment.method === 'acconto_saldo' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="% Acconto"><Input type="number" value={data.payment.deposit_percent} onChange={(e) => setField('payment.deposit_percent', Number(e.target.value))} className="tabular-nums" /></Field>
                    <Field label="% Saldo"><Input type="number" value={data.payment.balance_percent} onChange={(e) => setField('payment.balance_percent', Number(e.target.value))} className="tabular-nums" /></Field>
                  </div>
                )}
                {data.payment.method === 'doppio_acconto_saldo' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="doppio-acconto-fields">
                    <Field label="% 1° Acconto"><Input type="number" value={data.payment.deposit_percent} onChange={(e) => setField('payment.deposit_percent', Number(e.target.value))} className="tabular-nums" data-testid="deposit-percent-input" /></Field>
                    <Field label="% 2° Acconto"><Input type="number" value={data.payment.second_deposit_percent} onChange={(e) => setField('payment.second_deposit_percent', Number(e.target.value))} className="tabular-nums" data-testid="second-deposit-percent-input" /></Field>
                    <Field label="% Saldo"><Input type="number" value={data.payment.balance_percent} onChange={(e) => setField('payment.balance_percent', Number(e.target.value))} className="tabular-nums" data-testid="balance-percent-input" /></Field>
                  </div>
                )}
                {data.payment.method === 'misto_bolletta' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="N. rate"><Input type="number" value={data.payment.installments_count} onChange={(e) => setField('payment.installments_count', Number(e.target.value))} className="tabular-nums" /></Field>
                    <Field label="Importo rata (€)"><Input type="number" step="0.01" value={data.payment.installments_amount} onChange={(e) => setField('payment.installments_amount', Number(e.target.value))} className="tabular-nums" /></Field>
                  </div>
                )}
                <Field label="Note di pagamento"><Textarea rows={2} value={data.payment.notes} onChange={(e) => setField('payment.notes', e.target.value)} /></Field>

                <Separator />
                <Field label="Luogo della stipula"><Input value={data.contract.place} onChange={(e) => setField('contract.place', e.target.value)} placeholder="Es. Milano" data-testid="contract-place" /></Field>
                <Field label="Data contratto"><Input type="date" value={data.contract.date} onChange={(e) => setField('contract.date', e.target.value)} /></Field>
              </CardContent>
            </Card>
          )}

          {step === 6 && (
            <Card>
              <CardHeader><CardTitle className="text-base">6. Consensi GDPR, firma e generazione PDF</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Consensi privacy e dichiarazioni</div>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.consents.clauses_approval_1341_1342} onCheckedChange={(v) => setField('consents.clauses_approval_1341_1342', !!v)} data-testid="consent-1341" /> Approvazione clausole ex artt. 1341 e 1342 c.c.</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.consents.vat_reduced_declaration} onCheckedChange={(v) => setField('consents.vat_reduced_declaration', !!v)} /> Dichiarazione IVA agevolata 10%</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.consents.installer_delegation} onCheckedChange={(v) => setField('consents.installer_delegation', !!v)} /> Delega all’impresa installatrice</label>
                  <label className="flex items-start gap-2 text-sm rounded-md border border-primary/30 bg-primary/5 p-2" data-testid="consent-noicer">
                    <Checkbox
                      className="mt-0.5"
                      checked={data.consents.noicer_membership}
                      onCheckedChange={(v) => setField('consents.noicer_membership', !!v)}
                    />
                    <span>
                      <strong>Adesione alla Comunità Energetica Rinnovabile Nazionale «NoiCer»</strong>
                      <span className="block text-xs text-muted-foreground">Il cliente aderisce alla CER NoiCer per condividere l’energia prodotta e beneficiare degli incentivi previsti dal D.Lgs. 199/2021 (facoltativo, revocabile in qualsiasi momento).</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.consents.marketing} onCheckedChange={(v) => setField('consents.marketing', !!v)} /> Consenso marketing (facoltativo)</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.consents.profiling} onCheckedChange={(v) => setField('consents.profiling', !!v)} /> Consenso profilazione (facoltativo)</label>
                </div>

                <Separator />
                <div>
                  <div className="text-sm font-medium mb-3">Modalità di firma del Cliente</div>
                  <Tabs value={signMode} onValueChange={setSignMode} className="w-full" data-testid="signature-mode-tabs">
                    <TabsList className="grid grid-cols-2 w-full max-w-md">
                      <TabsTrigger value="manual" data-testid="tab-manual"><FileSignature className="size-4 mr-1" />Firma manuale</TabsTrigger>
                      <TabsTrigger value="otp" data-testid="tab-otp"><KeyRound className="size-4 mr-1" />Firma con OTP</TabsTrigger>
                    </TabsList>

                    <TabsContent value="manual" className="pt-4 space-y-2">
                      <SignaturePad ref={customerSigRef} onChange={(v) => setField('signature.customer_signature_data_url', v)} value={data.signature.customer_signature_data_url} />
                      <p className="text-xs text-muted-foreground">Il cliente firma direttamente col dito, mouse o pennino sul dispositivo.</p>
                    </TabsContent>

                    <TabsContent value="otp" className="pt-4">
                      <div className="rounded-lg border bg-secondary/20 p-4 space-y-3" data-testid="otp-panel">
                        <div className="flex items-start gap-3">
                          <div className="size-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                            <KeyRound className="size-4 text-primary" />
                          </div>
                          <div className="text-sm">
                            <div className="font-medium">Firma elettronica con codice OTP</div>
                            <div className="text-xs text-muted-foreground">Invia un codice a 6 cifre all’email del cliente. Una volta verificato, il contratto sarà firmato con attestazione OTP.</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                          <div className="md:col-span-2">
                            <Label className="text-xs">Email del cliente</Label>
                            <Input
                              type="email"
                              value={otpEmail}
                              onChange={(e) => setOtpEmail(e.target.value)}
                              placeholder="mario.rossi@example.com"
                              className="h-9"
                              data-testid="otp-email-input"
                              disabled={otpVerified}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleSendOtp}
                            disabled={otpSending || otpVerified || !otpEmail}
                            data-testid="otp-send-button"
                          >
                            {otpSending ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Mail className="size-4 mr-1" />}
                            {otpSent ? 'Reinvia codice' : 'Invia codice'}
                          </Button>
                        </div>

                        {otpSent && !otpVerified && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end pt-1">
                            <div className="md:col-span-2">
                              <div className="flex items-center justify-between mb-1">
                                <Label className="text-xs">Codice ricevuto via email</Label>
                                {otpExpiresAt && (
                                  <span
                                    className={`text-[11px] font-mono tabular-nums px-2 py-0.5 rounded-full border ${otpExpired ? 'bg-destructive/10 text-destructive border-destructive/30' : otpRemainingMs < 60000 ? 'bg-amber-500/10 text-amber-700 border-amber-500/30' : 'bg-primary/10 text-primary border-primary/30'}`}
                                    data-testid="otp-timer"
                                  >
                                    ⏱ {otpExpired ? 'Scaduto' : otpRemainingLabel}
                                  </span>
                                )}
                              </div>
                              <Input
                                inputMode="numeric"
                                maxLength={6}
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="123456"
                                className="h-9 tracking-[0.4em] font-mono text-center text-lg"
                                data-testid="otp-code-input"
                                autoComplete="one-time-code"
                                disabled={otpExpired}
                              />
                              <p className="text-[11px] text-muted-foreground mt-1">
                                Codice inviato a <strong>{otpMaskedEmail || otpEmail}</strong>
                                {!otpExpired && otpExpiresAt && <> · valido ancora <strong className="tabular-nums">{otpRemainingLabel}</strong></>}
                                {otpExpired && <> · <span className="text-destructive font-medium">codice scaduto, richiedi un nuovo invio</span></>}
                              </p>
                            </div>
                            <Button
                              type="button"
                              onClick={handleVerifyOtp}
                              disabled={otpVerifying || otpCode.length !== 6 || otpExpired}
                              data-testid="otp-verify-button"
                            >
                              {otpVerifying ? <Loader2 className="size-4 mr-1 animate-spin" /> : <ShieldCheck className="size-4 mr-1" />}
                              Verifica
                            </Button>
                          </div>
                        )}

                        {otpVerified && (
                          <div className="rounded-md border border-primary/40 bg-primary/5 p-3 flex items-center gap-3" data-testid="otp-verified-badge">
                            <div className="size-8 rounded-full bg-primary text-primary-foreground grid place-items-center">
                              <Check className="size-4" />
                            </div>
                            <div className="text-sm">
                              <div className="font-medium">Codice verificato correttamente</div>
                              <div className="text-xs text-muted-foreground">Il contratto sarà firmato elettronicamente per <strong>{otpMaskedEmail || otpEmail}</strong>. Puoi ora generare il PDF firmato.</div>
                            </div>
                            <Button variant="ghost" size="sm" type="button" onClick={resetOtp} className="ml-auto" data-testid="otp-reset-button">Reimposta</Button>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" />
                    Firma del Fornitore
                  </div>
                  <div className="rounded-lg border bg-secondary/20 p-4 text-sm text-muted-foreground" data-testid="supplier-stamp-info">
                    Il contratto è considerato <strong className="text-foreground">automaticamente firmato dal Fornitore</strong>: nel PDF verrà stampato il timbro e firma dell’azienda caricato nel profilo partner. Se non ancora presente, il responsabile può caricarlo dalla scheda partner &rarr; sezione Timbro &amp; Firma.
                  </div>
                </div>

                <Separator />
                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-button"><Check className="size-4 mr-2" />Salva senza firma</Button>
                  <Button onClick={() => handleGeneratePdf('filled')} disabled={saving} variant="outline" data-testid="generate-filled-pdf"><FileDown className="size-4 mr-2" />Salva e Scarica PDF compilato</Button>
                  <Button onClick={() => handleGeneratePdf('signed')} disabled={saving} data-testid="generate-signed-pdf"><FileSignature className="size-4 mr-2" />Salva e Scarica PDF firmato</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={back} disabled={step === 1 || (agentMode && step === 2)} data-testid="wizard-back-button"><ChevronLeft className="size-4 mr-1" /> Indietro</Button>
            {step < (visibleSteps[visibleSteps.length - 1]?.id || STEPS.length) ? (
              <Button onClick={next} disabled={!canContinue()} data-testid="wizard-next-button">Continua <ChevronRight className="size-4 ml-1" /></Button>
            ) : (isQuoteMode && step === 5) ? (
              <Button
                onClick={handleGenerateQuote}
                disabled={data.products.length === 0}
                data-testid="wizard-generate-quote-button"
                className="bg-primary"
              >
                <Sparkles className="size-4 mr-1" /> Genera Preventivo
              </Button>
            ) : <div />}
          </div>
        </div>

        {/* Sticky summary */}
        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-20" data-testid="contract-wizard-summary">
            <CardHeader className="pb-2"><CardTitle className="text-base">Riepilogo</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Partner</div>
                {company ? (
                  <div className="flex items-center gap-2 mt-1">
                    {company.logo_data_url ? <img src={company.logo_data_url} alt="" className="size-8 rounded border bg-white object-contain p-0.5" /> : <Building2 className="size-4 text-muted-foreground" />}
                    <span className="font-medium truncate">{company.name}</span>
                  </div>
                ) : <div className="text-muted-foreground">— Non selezionato</div>}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Cliente</div>
                <div className="font-medium">{data.customer.first_name} {data.customer.last_name || <span className="text-muted-foreground">—</span>}</div>
                {data.customer.fiscal_code && <div className="font-mono text-xs text-muted-foreground">{data.customer.fiscal_code}</div>}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Immobile</div>
                <div>{data.property.address || <span className="text-muted-foreground">—</span>}</div>
                {data.property.city && <div className="text-xs text-muted-foreground">{data.property.city} {data.property.province && `(${data.property.province})`}</div>}
              </div>
              <Separator />
              <div>
                <div className="text-xs text-muted-foreground mb-1">Prodotti ({data.products.length})</div>
                {data.products.length === 0 ? <div className="text-muted-foreground text-xs">—</div> : (
                  <ul className="space-y-1 max-h-32 overflow-y-auto">
                    {data.products.map((p, i) => (
                      <li key={i} className="flex justify-between text-xs"><span className="truncate pr-2">{p.brand} {p.model || p.description}</span><span className="tabular-nums">{p.quantity}×</span></li>
                    ))}
                  </ul>
                )}
              </div>
              <Separator />
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span className="tabular-nums">{formatCurrency(totals.subtotal)}</span></div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    IVA {(() => {
                      const rates = new Set();
                      (data.products || []).forEach(p => {
                        const r = (p.vat_rate === null || p.vat_rate === undefined || p.vat_rate === '')
                          ? Number(data.vat_rate)
                          : Number(p.vat_rate);
                        if (!Number.isNaN(r)) rates.add(r);
                      });
                      if (rates.size <= 1) return `${data.vat_rate}%`;
                      return `(mista: ${Array.from(rates).sort((a,b)=>a-b).map(r => `${r}%`).join(', ')})`;
                    })()}
                  </span>
                  <span className="tabular-nums">{formatCurrency(totals.vat)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-base font-semibold"><span>Totale</span><span className="tabular-nums" data-testid="summary-total">{formatCurrency(totals.total)}</span></div>
              </div>
              {data.payment.method && (
                <div>
                  <div className="text-xs text-muted-foreground">Pagamento</div>
                  <Badge variant="secondary" className="mt-1">{(options.payment_methods || []).find(m => m.key === data.payment.method)?.label || data.payment.method}</Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <QuoteBusinessPlanDialog
        open={bpDialogOpen}
        onOpenChange={(o) => {
          setBpDialogOpen(o);
          // Se il dialog si chiude dopo una generazione riuscita, pulisci la bozza
          // (viene chiuso anche in caso di annullamento, ma questo è un compromesso accettabile:
          //  se l'utente ha davvero generato il preventivo, il servizio ha già salvato lo storico)
        }}
        payload={buildQuotePayload()}
        editQuoteId={editQuoteId}
        onGenerated={(res) => {
          clearDraft();
          if (isEditMode) {
            // Dopo modifica riuscita torna all'elenco preventivi
            navigate('/quotes');
          } else if (res?.id) {
            navigate('/quotes');
          }
        }}
      />
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? 'md:col-span-full' : ''}`}>
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);
